import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, ClipboardList, LoaderCircle, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { formatDate } from '@/_shared/lib/format';

import type { MeasurementPlan, MeasurementPlanStatus } from '../types';

const STATUS_FILTERS: { value: 'all' | MeasurementPlanStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'implementing', label: 'Implementing' },
  { value: 'implemented', label: 'Implemented' },
  { value: 'partial_failure', label: 'Partial failure' },
  { value: 'verified', label: 'Verified' },
];

function statusVariant(status: MeasurementPlanStatus): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'verified') return 'default';
  if (status === 'partial_failure') return 'destructive';
  if (status === 'draft') return 'secondary';
  return 'outline';
}

function summarizeChanges(plan: MeasurementPlan): string {
  const events = plan.spec?.events ?? [];
  let added = 0;
  let modified = 0;
  let removed = 0;
  for (const e of events) {
    if (e.change_type === 'add') added += 1;
    else if (e.change_type === 'modify') modified += 1;
    else if (e.change_type === 'remove') removed += 1;
  }
  const parts: string[] = [];
  if (added) parts.push(`${added} new`);
  if (modified) parts.push(`${modified} modified`);
  if (removed) parts.push(`${removed} removed`);
  return parts.length ? parts.join(', ') : 'No changes';
}

export function MeasurementPlanList(): React.ReactElement {
  usePageTitle('Plans');
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | MeasurementPlanStatus>('all');

  const { data, isLoading, error } = useQuery(orpc.measurementPlan.list.queryOptions({ input: {} }));

  const deleteMutation = useMutation(
    orpc.measurementPlan.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.measurementPlan.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.confirm('Delete this measurement plan?')) {
        deleteMutation.mutate({ id });
      }
    },
    [deleteMutation],
  );

  const allPlans = useMemo<MeasurementPlan[]>(() => (data?.plans ?? []) as MeasurementPlan[], [data?.plans]);

  const plans = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPlans.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!q) return true;
      return p.title.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false);
    });
  }, [allPlans, search, statusFilter]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-b px-6 py-4">
        <div className="mb-2 flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-foreground">Plans</h1>
          <div className="flex items-center gap-2">
            {allPlans.length > 0 && (
              <>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | MeasurementPlanStatus)}>
                  <SelectTrigger className="h-9 w-44">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <SearchInput value={search} onChange={setSearch} placeholder="Search plans..." className="max-w-xs" />
              </>
            )}
          </div>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Living artifacts that describe instrumentation changes and link them to pull requests.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <CircleAlert size={32} className="mb-4 text-red-500" />
            <h2 className="text-base font-medium text-foreground">Failed to load plans</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {error.message || 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        )}

        {!isLoading && !error && allPlans.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <ClipboardList size={48} className="mb-4 text-muted-foreground/30" />
            <h2 className="text-base font-medium text-foreground">No measurement plans yet</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              No measurement plans yet. Describe what you want to track in the chat to create one.
            </p>
          </div>
        )}

        {!isLoading && !error && allPlans.length > 0 && (
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 px-6 py-6 sm:grid-cols-2">
            {plans.map((plan) => (
              <Link
                key={plan.id}
                to={wp(`/plans/${plan.id}`)}
                className="group block transition-opacity hover:opacity-80"
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">{plan.title}</CardTitle>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge variant={statusVariant(plan.status)} className="text-[0.625rem]">
                          {plan.status}
                        </Badge>
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, plan.id)}
                          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                          aria-label={`Delete ${plan.title}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {plan.description && (
                      <CardDescription className="line-clamp-2 text-xs">{plan.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{summarizeChanges(plan)}</span>
                      <span>{formatDate(plan.updatedAt)}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
